import type { ToastData } from '@rtkwlf/fenrir-react';
import { captureException } from '@sentry/react';
import { keepPreviousData, QueryClient } from '@tanstack/react-query';

import { env } from '@packages/utils/env';

const commonAPIErrors = ['ResponseError', 'FetchError'];

export type ErrorToastData = Omit<ToastData, 'id'>;

export type OnErrorToastData =
  | ErrorToastData
  | ((error: Error) => Promise<ErrorToastData | undefined>);

interface Meta extends Record<string, unknown> {
  onErrorToastData?: OnErrorToastData;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: Meta;
    mutationMeta: Meta;
  }
}

/**
 * By default, errors triggered inside of a query or mutation are not thrown and instead handled through the `error` state.
 * Since this behavior is not limited to API errors, other errors (e.g. TypeError) may go unnoticed and/or be harder to debug.
 * The purpose of this function is to surface these errors.
 *
 * Common API errors are neither thrown nor logged, all other errors are logged to Sentry and thrown in development mode for visibility.
 */
const throwOnError = (error: Error) => {
  if (commonAPIErrors.includes(error.name)) return false;

  if (env.MODE === 'development' || env.MODE === 'test') {
    // eslint-disable-next-line no-console
    console.warn(
      `This error (${error.name}: ${error.message}) is being surfaced for visibility in development mode. ` +
        'It is NOT surfaced in production.\n\n',
    );

    return true;
  }

  captureException(error, { tags: { platform: 'query-client' } });
  return false;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      throwOnError,
    },
    queries: {
      retry: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
      throwOnError,
    },
  },
});
